import React from 'react';
import './Radio.css';

export interface RadioProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  /** Label text displayed next to radio button */
  label?: React.ReactNode;
  /** Alternative to label prop - renders children as label content */
  children?: React.ReactNode;
  /** Validation state */
  state?: 'default' | 'error';
  /** Radio button size */
  size?: 'sm' | 'md' | 'lg';
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      children,
      state = 'default',
      size = 'md',
      checked,
      defaultChecked,
      disabled,
      required,
      className = '',
      style,
      ...rest
    },
    ref
  ) => {
    const labelContent = label || children;

    const classes = [
      'hi-ui-radio',
      `hi-ui-radio-size-${size}`,
      state === 'error' && 'hi-ui-radio-state-error',
      disabled && 'hi-ui-radio-disabled',
      (checked || defaultChecked) && 'hi-ui-radio-checked',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={classes} style={style}>
        <input
          ref={ref}
          type="radio"
          className="hi-ui-radio-input"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          required={required}
          aria-invalid={state === 'error' ? true : undefined}
          aria-required={required ? true : undefined}
          {...rest}
        />
        <span className="hi-ui-radio-circle" aria-hidden="true">
          <span className="hi-ui-radio-dot" />
        </span>
        {labelContent && <span className="hi-ui-radio-label">{labelContent}</span>}
      </label>
    );
  }
);

Radio.displayName = 'Radio';
